import { supabase } from "../supabase/SupabaseClient";

/* Multas de tránsito (PBA/CABA/SINAI), vía fibot-vehicle-intelligence.
 *
 * Dos pasos separados a propósito: `consultarMultas` sólo pega afuera (la
 * edge function, que no toca la base) y `registrarConsulta` es el que
 * guarda -- así el guardado corre con el JWT de quien está mirando la
 * pantalla, y `multas_registrar_consulta` puede verificar `cargar.documentos`
 * de verdad en vez de confiar en lo que ya pasó por la edge function.
 */

// Igual forma que `consultarScania`: siempre { data, error }, con el error
// legible y no el genérico del SDK.
export const consultarMultas = async (dominio) => {
  try {
    const { data, error: fnError } = await supabase.functions.invoke(
      "consultar-multas",
      { body: { dominio } },
    );

    if (fnError || data?.error) {
      let mensajeReal = data?.error;
      if (!mensajeReal && fnError?.context?.json) {
        try {
          const body = await fnError.context.json();
          mensajeReal = body?.error;
        } catch {
          // el body no era JSON (ej. timeout del gateway) -- seguimos con
          // fnError.message.
        }
      }
      return {
        data: null,
        error: mensajeReal || fnError?.message || "Error desconocido",
      };
    }

    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message || "Error desconocido" };
  }
};

// Guarda lo que trajo `consultarMultas`. `resultado` es la respuesta cruda
// de fibot-vehicle-intelligence: { penalties, provider_status, warnings }.
export async function registrarConsulta(dominio, resultado) {
  const { error } = await supabase.rpc("multas_registrar_consulta", {
    p_dominio: dominio,
    p_penalidades: resultado?.penalties || [],
    p_estados: resultado?.provider_status || {},
    p_avisos: resultado?.warnings || [],
  });
  if (error) throw new Error(error.message);
}

// Consulta en vivo y guarda de una. Es lo que usa el botón de la ficha.
export async function consultarYRegistrar(dominio) {
  const { data, error } = await consultarMultas(dominio);
  if (error) throw new Error(error);
  await registrarConsulta(dominio, data);
  return data;
}

export async function multasDeUnidad(dominio) {
  const [actas, consulta] = await Promise.all([
    supabase.rpc("multas_de_unidad", { p_dominio: dominio }),
    supabase.rpc("multas_consulta_de", { p_dominio: dominio }),
  ]);
  if (actas.error) throw new Error(actas.error.message);
  if (consulta.error) throw new Error(consulta.error.message);
  return { actas: actas.data || [], consulta: consulta.data?.[0] || null };
}

/* Un acta sola, por id. La pide el buscador (`?ver=`): la pantalla de Multas
 * lista UNIDADES, así que del acta interesa sobre todo el dominio, para saber
 * qué fila (y de qué flota) mostrar. La RLS de `multas` decide si se ve. */
export async function multaPorId(id) {
  const { data, error } = await supabase
    .from("multas")
    .select(
      "id, dominio, jurisdiccion, acta, ocurrida_en, lugar, descripcion, monto, pagada, discrepancia",
    )
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data || null;
}

export async function marcarMultaPagada(id, pagada) {
  const { error } = await supabase.rpc("multas_marcar_pagada", {
    p_id: id,
    p_pagada: pagada,
  });
  if (error) throw new Error(error.message);
}

export async function marcarTodasPagadas(dominio) {
  const { error } = await supabase.rpc("multas_marcar_todas_pagadas", {
    p_dominio: dominio,
  });
  if (error) throw new Error(error.message);
}

export async function multasTractores() {
  const { data, error } = await supabase.rpc("multas_tractores");
  if (error) throw new Error(error.message);
  return data || [];
}

export async function multasFurgones() {
  const { data, error } = await supabase.rpc("multas_furgones");
  if (error) throw new Error(error.message);
  return data || [];
}
